<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <v-card v-cloak>
      <v-card-title class="d-flex justify-space-between">
        {{ $t('console.manageTenantPermissions') }}

        <v-btn icon @click="dialog = false;">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text scrollable class="scrollable mt-4">
        <v-form ref="form">
          <!-- Select nodes to share -->
          <div class="mb-4">
            {{ $t('console.selectFolderToShare') }}

            <SelectNodeDialog
              v-model="selectedNodeId"
              #default="slotProps"
              :disabled-node-ids="selectedNodeIdsArray"
            >
              <v-btn variant="flat" block color="info">
                <span v-if="slotProps && slotProps.selectedNode">
                  {{ slotProps.selectedNode.name }}
                </span>
                <span v-else>
                  {{ $t('console.selectFolder') }}
                </span>
              </v-btn>
            </SelectNodeDialog>
          </div>

          <!-- List of the selected nodes -->
          <div>
            <p v-if="selectedNodeIds.size > 0">
              {{ $t('console.thisFoldersWillBeShared') }}
            </p>
            <p v-else class="mb-4">
              {{ $t('console.noFolderSelected') }}
            </p>
            <v-list v-if="selectedNodeIds.size > 0" compact>
              <v-list-item
                v-for="node in selectedNodes"
                :key="node.id"
              >
                <v-list-item-title>
                  {{ node.name }}
                </v-list-item-title>

                <template v-slot:append>
                  <v-btn
                    icon="mdi-close"
                    width="28px"
                    height="28px"
                    @click="removeSelectedNode(node.id)"
                    variant="text"
                  />
                </template>
              </v-list-item>
            </v-list>
          </div>

          <div class="pt-5 pb-5">
            <v-row>
              <v-col cols="12" sm="6" offset-sm=3>
                <v-list-subheader>
                  <h3>{{ $t('menus.userPermissions.nodesAndPods') }}</h3>
                </v-list-subheader>

                <div class="d-flex align-center" v-if="$canListNodeAndPod()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="nodeAndPodList"
                    color="primary"
                    :label="$t('menus.userPermissions.listNodesAndPods')"
                    density="compact"
                    hide-details
                    block
                  />
                </div>

                <div class="d-flex align-center" v-if="$canControlNodeAndPod()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="nodeAndPodControl"
                    color="primary"
                    :label="$t('menus.userPermissions.controlNodesAndPods')"
                    density="compact"
                    hide-details
                    block
                  />
                </div>

                <div class="d-flex align-center" v-if="$canDeleteNodeAndPod()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="nodeAndPodDelete"
                    :label="$t('menus.userPermissions.deleteNodesAndPods')"
                    hide-details
                    color="primary"
                    density="compact"
                    block
                  />
                </div>

                <div class="d-flex align-center" v-if="$canDownloadNodeAndPod()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="nodeAndPodDownload"
                    color="primary"
                    :label="$t('menus.userPermissions.downloadNodesAndPods')"
                    density="compact"
                    hide-details
                    block
                  />
                </div>

                <div class="d-flex align-center" v-if="$canListAsset()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="assetList"
                    color="primary"
                    :label="$t('menus.userPermissions.showPodAssets')"
                    density="compact"
                    hide-details
                    block
                  />
                </div>

                <div class="d-flex align-center" v-if="$canUploadFileAndAsset()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="fileAndAssetUpload"
                    color="primary"
                    :label="$t('menus.userPermissions.uploadFilesAndAssets')"
                    density="compact"
                    hide-details
                    block
                  />
                </div>

                <div class="d-flex align-center" v-if="$canListShareLinks()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="listShareLinks"
                    color="primary"
                    :label="$t('menus.userPermissions.listShareLinks')"
                    density="compact"
                    hide-details
                    block
                  />
                </div>

                <div class="d-flex align-center" v-if="$canControlShareLinks()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="controlShareLinks"
                    color="primary"
                    :label="$t('menus.userPermissions.controlShareLinks')"
                    density="compact"
                    hide-details
                    block
                  />
                </div>

                <div class="d-flex align-center" v-if="$canArchiveNodeAndPod()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="nodeAndPodArchive"
                    color="primary"
                    :label="$t('menus.userPermissions.archiveNodesAndPods')"
                    density="compact"
                    hide-details
                    block
                  />
                </div>

                <div class="d-flex align-center" v-if="$canViewPodAndNodeAnalytics()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="viewAnalytics"
                    color="primary"
                    :label="$t('menus.userPermissions.viewAnalytics')"
                    density="compact"
                    hide-details
                    block
                  />
                </div>

                <div class="d-flex align-center" v-if="$canLabelPods()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="podsLabel"
                    color="primary"
                    :label="$t('menus.userPermissions.labelPods')"
                    density="compact"
                    hide-details
                    block
                  />
                </div>
              </v-col>
              <v-col cols="12" sm="6" offset-sm=3>
                <v-list-subheader>
                  <h3>{{ $t('menus.userPermissions.services') }}</h3>
                </v-list-subheader>

                <div class="d-flex align-center" v-if="$canListPodService()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="podServiceList"
                    color="primary"
                    :label="$t('menus.userPermissions.listPodServices')"
                    density="compact"
                    hide-details
                  />
                </div>

                <div class="d-flex align-center" v-if="$canControlPodService()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="podServiceControl"
                    color="primary"
                    :label="$t('menus.userPermissions.controlPodServices')"
                    density="compact"
                    hide-details
                  />
                </div>

                <div class="d-flex align-center" v-if="$canRunFreeService()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="useFreeServices"
                    color="primary"
                    :label="$t('menus.userPermissions.useFreeServices')"
                    density="compact"
                    hide-details
                  />
                </div>

                <div class="d-flex align-center" v-if="$canRunPaidService()">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="usePaidServices"
                    color="primary"
                    :label="$t('menus.userPermissions.usePaidServices')"
                    density="compact"
                    hide-details
                  />
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Tenant admin email input -->
          <div v-if="tenantConnection.connectionCode" class="mt-4">
            <p>{{ $t('console.connectioncode') }}:</p>
            <v-text-field
              :model-value="tenantConnection.connectionCode"
              :placeholder="$t('console.connectioncode')"
              :rules="[]"
              readonly
              variant="solo-filled"
              hide-details="auto"
              block
              flat
              data-cy="tenantAdminEmailInput"
            >

              <template v-slot:append>
                <v-tooltip location="bottom" z-index="1200">
                  <template v-slot:activator="{props}">
                    <v-btn icon v-bind="props" elevation="0"
                           v-on:click="copyToClipboard(tenantConnection.connectionCode)">
                      <v-icon>mdi-clipboard-file-outline</v-icon>
                    </v-btn>
                  </template>

                  <span>{{ $t('menus.authentication.copyToClipboard') }}</span>
                </v-tooltip>
              </template>
            </v-text-field>
          </div>

          <div v-if="errorMessage" class="bg-error my-2" data-cy="-----">
            {{ errorMessage }}
          </div>

          <div v-if="successMessage" class="bg-success pa-1 mt-1" data-cy="------">
            {{ successMessage }}
          </div>
        </v-form>
      </v-card-text>


      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn type="submit" class="bg-primary" elevation=0 v-bind:loading="isSubmitted"
               v-bind:disabled="isSubmitted" @click="submitForm">
          {{ $t('common.save') }}
        </v-btn>
        <v-btn variant="text" @click="dialog = false">{{ $t('common.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useTenantConnectionsStore } from '@/stores/TenantConnectionsStore.js';
import SelectNodeDialog from '../../../users/components/dialogs/SelectNodeDialog.vue';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';


export default {
  mixins: [ DialogActionKeys, DialogModel ],
  name: 'HomeTenantPermissionsDialog',
  components: {
    SelectNodeDialog,
  },
  props: {
    tenantConnection: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isSubmitted: false,
      errorMessage: '',
      successMessage: '',

      selectedNodeId: null,
      selectedNodeIds: new Set(),

      // Permissions - Initialized from prop or defaults
      nodeAndPodList: true,
      nodeAndPodControl: true,
      nodeAndPodDelete: true,
      nodeAndPodDownload: true,
      assetList: true,
      fileAndAssetUpload: true,
      listShareLinks: true,
      controlShareLinks: true,
      nodeAndPodArchive: true,
      viewAnalytics: true,
      podsLabel: true,

      podServiceList: true,
      podServiceControl: true,
      useFreeServices: false,
      usePaidServices: false,
    };
  },

  created() {
    this.initializeData();
  },

  watch: {
    dialog() {
      if (this.dialog) {
        this.initializeData();
      }
    },
    selectedNodeId(newVal) {
      if (newVal) {
        this.selectedNodeIds.add(newVal);

        this.collapseSelectedNodeChildren(newVal);
        this.discardSelectedNodeParents(newVal);
      }
    },
    tenantConnection: {
      handler() {
        this.initializeData();
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    initializeData() {
      this.errorMessage = '';
      this.successMessage = '';
      this.isSubmitted = false;

      if (this.tenantConnection) {
        this.updateControls();
      } else {
        // Default values if no tenantConnection is provided (shouldn't happen for this dialog)
        this.selectedNodeIds = new Set();

        this.nodeAndPodList = true;
        this.nodeAndPodControl = true;
        this.nodeAndPodDelete = true;
        this.nodeAndPodDownload = true;
        this.podsLabel = true;
        this.assetList = true;
        this.fileAndAssetUpload = true;
        this.listShareLinks = true;
        this.controlShareLinks = true;
        this.nodeAndPodArchive = true;
        this.viewAnalytics = true;
        this.podServiceList = true;
        this.podServiceControl = true;
        this.useFreeServices = false;
        this.usePaidServices = false;
      }
    },

    resetForm() {
      this.initializeData(); // Re-initialize data based on the current tenantConnection prop
    },
    removeSelectedNode(nodeId) {
      this.selectedNodeIds.delete(nodeId);
    },
    async submitForm() {
      const { valid } = await this.$refs.form.validate();

      if (!valid || this.selectedNodeIds.size === 0) {
        if (this.selectedNodeIds.size === 0) {
          this.errorMessage = this.$t('console.noFolderSelected');
        }
        return;
      }

      if (!this.tenantConnection || !this.tenantConnection.id) {
        this.errorMessage = this.$t('console.tenantConnectionNotFound');
        return;
      }

      // Prepare the data for the API call
      const formData = {
        tenantConnectionId: this.tenantConnection.id,
        selectedNodeIds: Array.from(this.selectedNodeIds),
        permissions: [
          this.nodeAndPodList && 'NodeAndPod.list',
          this.nodeAndPodControl && 'NodeAndPod.control',
          this.nodeAndPodDelete && 'NodeAndPod.delete',
          this.nodeAndPodDownload && 'NodeAndPod.download',
          this.assetList && 'Asset.list',
          this.fileAndAssetUpload && 'FileAndAsset.upload',
          this.listShareLinks && 'ShareLinks.list',
          this.controlShareLinks && 'ShareLinks.control',
          this.nodeAndPodArchive && 'NodeAndPod.archive',
          this.viewAnalytics && 'NodeAndPod.viewAnalytics',
          this.podsLabel && 'Pod.label',
          this.podServiceList && 'PodService.list',
          this.podServiceControl && 'PodService.control',
          this.useFreeServices && 'FreeService.run',
          this.usePaidServices && 'PaidService.run',
        ].filter(Boolean),
      };

      try {
        this.isSubmitted = true;
        const tenantConnectionsStore = useTenantConnectionsStore();
        await tenantConnectionsStore.updateHomeTenantConnection(this.rootNodeId, this.tenantConnection.id, formData);
        this.dialog = false;
        await tenantConnectionsStore.refreshHomeTenantsList(this.rootNodeId);
      } catch (error) {
        console.error(error);

        if (error?.response?.data?.error) {
          this.errorMessage = this.$localizeMessage(error.response.data.error);
        } else {
          this.errorMessage = this.$t('backend.system-error');
        }
      } finally {
        this.isSubmitted = false;
      }
    },
    updateControls: function () {
      if (!this.tenantConnection) {
        return;
      }

      const mainGroup = this.tenantConnection.groups.find(group => group.parentid === null);
      if (!mainGroup) {
        return;
      }

      const abilitiesList = mainGroup.abilities || [];

      this.nodeAndPodList = this.$canListNodeAndPod() &&
        this.findAbility(abilitiesList, 'list', 'NodeAndPod');
      this.nodeAndPodControl = this.$canControlNodeAndPod() &&
        this.findAbility(abilitiesList, 'control', 'NodeAndPod');
      this.nodeAndPodDelete = this.$canDeleteNodeAndPod() &&
        this.findAbility(abilitiesList, 'delete', 'NodeAndPod');
      this.nodeAndPodDownload = this.$canDownloadNodeAndPod() &&
        this.findAbility(abilitiesList, 'download', 'NodeAndPod');
      this.assetList = this.$canListAsset() &&
        this.findAbility(abilitiesList, 'list', 'Asset');
      this.fileAndAssetUpload = this.$canUploadFileAndAsset() &&
        this.findAbility(abilitiesList, 'upload', 'FileAndAsset');
      this.listShareLinks = this.$canListShareLinks() &&
        this.findAbility(abilitiesList, 'list', 'ShareLinks');
      this.controlShareLinks = this.$canControlShareLinks() &&
        this.findAbility(abilitiesList, 'control', 'ShareLinks');
      this.nodeAndPodArchive = this.$canArchiveNodeAndPod() &&
        this.findAbility(abilitiesList, 'archive', 'NodeAndPod');
      this.viewAnalytics = this.$canViewPodAndNodeAnalytics() &&
        this.findAbility(abilitiesList, 'viewAnalytics', 'NodeAndPod');
      this.podsLabel = this.$canLabelPods() &&
        this.findAbility(abilitiesList, 'label', 'Pod');

      this.podServiceList = this.$canListPodService() &&
        this.findAbility(abilitiesList, 'list', 'PodService');
      this.podServiceControl = this.$canControlPodService() &&
        this.findAbility(abilitiesList, 'control', 'PodService');
      this.useFreeServices = this.$canRunFreeService() &&
        this.findAbility(abilitiesList, 'run', 'FreeService');
      this.usePaidServices = this.$canRunPaidService() &&
        this.findAbility(abilitiesList, 'run', 'PaidService');

      this.selectedNodeIds = new Set(this.tenantConnection.nodes.map(node => node.id));

      const nodeStore = useNodeStore();
      for (const nodeId of this.selectedNodeIds) {
        const node = nodeStore.getNodeById(nodeId);
        if (!node) {
          nodeStore.getNodeInfo(nodeId);
        }
      }
    },
    findAbility: function (abilities, action, subject) {
      for (let i = 0; i < abilities.length; i++) {
        if (abilities[i].action === action && abilities[i].subject === subject) {
          return true;
        }
      }
      return false;
    },
    copyToClipboard: function (url) {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(url);
      } else {
        // it works only on localhost and hosts with https
        // console.log("Copy not supported")
      }
    },

    /**
     * Collapses multiple child nodes that are descendants of the selected one by the long-most
     * root path of the given node.
     *
     * @param {string} selectedNodeId
     */
    collapseSelectedNodeChildren(selectedNodeId) {
      const nodeStore = useNodeStore();
      const selectedNode = nodeStore.getNodeById(selectedNodeId);
      if (!selectedNode) return;
      const selectedNodeRootPath = selectedNode.rootpath;

      for (const node of this.selectedNodes) {
        if (node.id === selectedNode.id) continue;
        if (node.rootpath.startsWith(selectedNodeRootPath)) this.selectedNodeIds.delete(node.id);
      }
    },
    /**
     * Discard any parents of already selected nodes that are their children.
     *
     * @param {string} selectedNodeId
     */
    discardSelectedNodeParents(selectedNodeId) {
      const nodeStore = useNodeStore();
      const selectedNode = nodeStore.getNodeById(selectedNodeId);
      if (!selectedNode || !selectedNode.parentid) return;
      const selectedNodeRootPath = selectedNode.rootpath;

      for (const node of this.selectedNodes) {
        if (node.rootpath.length < selectedNodeRootPath.length && selectedNodeRootPath.startsWith(node.rootpath)) {
          this.selectedNodeIds.delete(node.id);
        }
      }
    }
  },
  computed: {
    rootNodeId: function () {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    selectedNodes() {
      const nodeStore = useNodeStore();
      return Array.from(this.selectedNodeIds)
        .map(nodeId => nodeStore.getNodeById(nodeId))
        .filter(node => node !== undefined);
    },
    selectedNodeIdsArray() {
      return Array.from(this.selectedNodeIds.values());
    }
  },
};
</script>
